import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Cannabis, CheckCircle, MapPin, Phone, Globe, Clock, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API = "/api";

const LICENSE_TYPES = [
  { value: 'recreational', label: 'Recreational (Adult-Use)' },
  { value: 'medical', label: 'Medical Only' },
  { value: 'both', label: 'Both (Medical & Recreational)' }
];

const CITIES = [
  'Minneapolis', 'St. Paul', 'Bloomington', 'Brooklyn Park', 'Plymouth',
  'Woodbury', 'Maple Grove', 'Eagan', 'Eden Prairie', 'Burnsville',
  'Duluth', 'Rochester', 'Other'
];

const RegisterDispensary = () => {
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    license_number: '',
    license_type: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip_code: '',
    description: '',
    website: '',
    instagram: '',
    menu_url: '',
    offers_delivery: false,
    offers_pickup: true,
    agreed_to_terms: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.agreed_to_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = { ...form };
      delete submitData.agreed_to_terms;
      
      const response = await axios.post(`${API}/dispensaries/register`, submitData);
      setRegistrationId(response.data.id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">Registration Submitted!</h1>
          <p className="text-muted-foreground mb-2">
            Thank you for registering your dispensary with Twin Tube's Canna-Club.
          </p>
          <p className="text-muted-foreground mb-6">
            Your registration ID is: <span className="font-mono text-primary">{registrationId}</span>
          </p>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm mb-6">
            <p className="text-sm text-yellow-200">
              <strong>What happens next?</strong><br />
              Our team will verify your Minnesota cannabis license and review your submission. 
              This typically takes 1-3 business days. You'll receive an email notification once approved.
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <Button asChild variant="outline">
              <Link to="/">Back to Home</Link>
            </Button>
            <Button asChild className="bg-green-600 hover:bg-green-700">
              <Link to="/canna-club">View Canna-Club</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <Link to="/canna-club" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft size={16} /> Back to Canna-Club
      </Link>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-sm bg-green-500/20 flex items-center justify-center">
            <Cannabis size={24} className="text-green-500" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold">Register Your Dispensary</h1>
            <p className="text-muted-foreground">Join the Twin Tube Canna-Club</p>
          </div>
        </div>

        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm my-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-green-500 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-green-200">Licensed Dispensaries Only</p>
              <p className="text-sm text-muted-foreground">
                Only Minnesota-licensed cannabis dispensaries may register. Your license will be verified before approval.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Business Information */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Business Information</CardTitle>
              <CardDescription>Basic information about your dispensary</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Dispensary Name *</Label>
                  <Input
                    id="business_name"
                    value={form.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    placeholder="Green Leaf Dispensary"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_number">MN License Number *</Label>
                  <Input
                    id="license_number"
                    value={form.license_number}
                    onChange={(e) => handleChange('license_number', e.target.value)}
                    placeholder="MN-CANN-XXXXX"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="license_type">License Type *</Label>
                <Select
                  value={form.license_type}
                  onValueChange={(value) => handleChange('license_type', value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select license type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Tell us about your dispensary, products, and what makes you unique..."
                  rows={4}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
              <CardDescription>How we can reach you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contact@dispensary.com"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(612) 555-0123"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin size={18} /> Location
              </CardTitle>
              <CardDescription>Your dispensary's physical location</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Select
                    value={form.city}
                    onValueChange={(value) => handleChange('city', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={form.zip_code}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                    placeholder="55401"
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Online Presence */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe size={18} /> Online Presence
              </CardTitle>
              <CardDescription>Your website and social media (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={form.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://yourdispensary.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram Handle</Label>
                <Input
                  id="instagram"
                  value={form.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder="@yourdispensary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="menu_url">Online Menu URL</Label>
                <Input
                  id="menu_url"
                  type="url"
                  value={form.menu_url}
                  onChange={(e) => handleChange('menu_url', e.target.value)}
                  placeholder="https://weedmaps.com/dispensaries/..."
                />
                <p className="text-xs text-muted-foreground">Link to your menu on Weedmaps, Dutchie, Leafly, etc.</p>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Services Offered</CardTitle>
              <CardDescription>What services do you provide?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="offers_pickup"
                  checked={form.offers_pickup}
                  onCheckedChange={(checked) => handleChange('offers_pickup', checked)}
                />
                <Label htmlFor="offers_pickup" className="cursor-pointer">In-Store Pickup</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="offers_delivery"
                  checked={form.offers_delivery}
                  onCheckedChange={(checked) => handleChange('offers_delivery', checked)}
                />
                <Label htmlFor="offers_delivery" className="cursor-pointer">Delivery Service</Label>
              </div>
            </CardContent>
          </Card>

          {/* Terms */}
          <Card className="bg-card/50 border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="agreed_to_terms"
                  checked={form.agreed_to_terms}
                  onCheckedChange={(checked) => handleChange('agreed_to_terms', checked)}
                  className="mt-1"
                />
                <Label htmlFor="agreed_to_terms" className="cursor-pointer text-sm leading-relaxed">
                  I confirm that I am an authorized representative of this dispensary and that we hold a valid 
                  Minnesota cannabis license. I understand that our license will be verified before our listing 
                  is approved. I agree to the Twin Tube Terms of Service and Community Guidelines.
                </Label>
              </div>
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700" 
            size="lg"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterDispensary;
